import {
  Box,
  Divider,
  Grid,
  Heading,
  HStack,
  Select,
  Text,
  VStack
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useIsMobile from '@hooks/useIsMobile';
import ContentLayout from '@layouts/ContentLayout';
import { getGomsFunnelOrders } from '@store/actions/funnelActions';
import {
  updateGomsFunnelOrdersReqData,
  updateGomsFunnelReqData
} from '@store/actions/reqDataActions';
import FunnelBar from './FunnelBar';
import { formatFunnelStatus } from './utils';
import {
  GOMS_ORDERS_STATUS,
  FILTER_BY_PAST_HOURS,
  ORDER_ACQUIRER,
  ORDER_RESOURCE
} from '@constants';
import useDidMountEffect from '@hooks/useDidMount';
import OrderDetailsCard from '@components/OrderDetailsCard';
import OrdersList from '@components/OrdersList';
import { useLocation, useNavigate } from 'react-router-dom';

const GomsOrderFunnel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const orders = useSelector((state) => state.funnel.gomsFunnel.tabData);
  const tabData = useSelector((state) => state.funnel.gomsFunnel.tabCount);

  const isMobile = useIsMobile();
  const searchReqData = useSelector(
    (state) => state.reqData.funnel.gomsFunnel.funnelReqData
  );
  const { activeFunnel, acquirer, ordersInPastHours, orderType } = useSelector(
    (state) => state.reqData.funnel.gomsFunnel
  );
  const [selectedOrder, setSelectedOrder] = useState(null);

  const location = useLocation();

  const putQueryParams = (params) => {
    const searchParams = new URLSearchParams();
    Object.keys(params)?.map((key) => {
      if (!params[key]) return;
      searchParams.append(key, params[key]);
    });
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const loadQueryParams = () => {
    const queryParams = new URLSearchParams(location.search);
    const orderType = queryParams.get('orderType');
    const acquirer = queryParams.get('acquirer');
    const ordersInPastHours = queryParams.get('ordersInPastHours');
    const activeFunnel = queryParams.get('activeFunnel');
    const params = {
      orderType,
      acquirer,
      ordersInPastHours,
      activeFunnel
    };
    const reduced = Object.keys(params).reduce((prev, curr) => {
      if (!params[curr]) return { ...prev };
      return {
        ...prev,
        [curr]: params[curr]
      };
    }, {});
    Object.keys(reduced).length > 0 &&
      dispatch(updateGomsFunnelReqData(reduced));
  };

  useEffect(() => {
    loadQueryParams();
    triggerGetAllFunnelOrders();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const prevActiveFunnel = useRef(null);
  useDidMountEffect(() => {
    if (prevActiveFunnel.current === activeFunnel) searchFunnelOrders(true);
    else searchFunnelOrders();
    prevActiveFunnel.current = activeFunnel;
  }, [searchReqData[activeFunnel]]);

  useDidMountEffect(() => {
    triggerGetAllFunnelOrders(true);
    putQueryParams({
      acquirer,
      ordersInPastHours,
      orderType,
      activeFunnel
    });
  }, [acquirer, ordersInPastHours, orderType]);

  // Watch for page changes and trigger API request
  // This ensures that when the page state changes in Redux, a new API request is made
  useDidMountEffect(() => {
    const currentPageNo = searchReqData[activeFunnel]?.pageNo || 0;
    dispatch(
      getGomsFunnelOrders(
        activeFunnel,
        acquirer,
        { ...searchReqData[activeFunnel], pageNo: currentPageNo },
        ordersInPastHours,
        orderType
      )
    );
  }, [searchReqData[activeFunnel]?.pageNo]);

  const triggerGetAllFunnelOrders = (refresh) => {
    GOMS_ORDERS_STATUS.forEach((status) => {
      if (refresh || !orders[status]?.data?.length) {
        dispatch(
          getGomsFunnelOrders(
            status,
            acquirer,
            searchReqData[status] || {},
            ordersInPastHours,
            orderType
          )
        );
      }
    });
  };

  const searchFunnelOrders = (refresh = false) => {
    if (refresh || !orders[activeFunnel]?.data?.length)
      dispatch(
        getGomsFunnelOrders(
          activeFunnel,
          acquirer,
          searchReqData[activeFunnel],
          ordersInPastHours
        )
      );
  };

  /**
   * Handles page changes in pagination
   * @param {number} pageNo - 1-based page number from UI
   */
  const handlePageChange = (pageNo) => {
    const newPageNo = pageNo - 1; // Convert from 1-based to 0-based indexing
    const currentPageNo = searchReqData[activeFunnel]?.pageNo || 0;
    
    // Only update if the page actually changed to avoid unnecessary re-renders
    if (newPageNo !== currentPageNo) {
      dispatch(
        updateGomsFunnelOrdersReqData(activeFunnel, {
          ...searchReqData[activeFunnel],
          pageNo: newPageNo
        })
      );
    }
  };

  const onOrderDetailsClick = useCallback(
    (orderId, userId, orderResource) => {
      if (isMobile)
        navigate(`/order/${orderId}?orderResource=${orderResource}`);

      setSelectedOrder({
        orderId,
        userId,
        orderResource
      });
    },
    [isMobile, navigate]
  );

  const handleAcquirerChange = (e) =>
    e.target.value !== acquirer &&
    dispatch(updateGomsFunnelReqData({ acquirer: e.target.value }));

  const handleOrdersInPastHours = (e) => {
    const pastHours = e?.target?.value;
    pastHours !== ordersInPastHours &&
      dispatch(updateGomsFunnelReqData({ ordersInPastHours: pastHours }));
  };

  const handleActiveFunnelChange = (status) => {
    if (status !== activeFunnel) {
      dispatch(updateGomsFunnelReqData({ activeFunnel: status }));
      putQueryParams({
        acquirer,
        ordersInPastHours,
        orderType,
        activeFunnel: status
      });
    }
  };

  return (
    <ContentLayout>
      <Grid gridTemplateColumns={{ base: '1fr', lg: '7fr 0fr 3fr' }}>
        <VStack
          gap={4}
          overflowY={{ base: 'initial', lg: 'scroll' }}
          overflowX={'hidden'}
          h={{ lg: '90vh' }}
          py={10}
          px={{ base: 4, sm: 10 }}
          alignItems={'stretch'}
          className="scroll"
        >
          <FunnelBar
            setActiveFunnel={handleActiveFunnelChange}
            activeFunnel={activeFunnel}
            funnelData={tabData}
            isListLoading={orders[activeFunnel]?.isLoading}
          />
          <HStack justifyContent={'space-between'} gap={4}>
            <Heading
              color={'white'}
              fontSize={'1.5rem'}
              fontWeight={500}
              pt={3}
            >
              {formatFunnelStatus(activeFunnel)}
            </Heading>
            <VStack gap={2} alignItems={'flex-end'}>
              <Box maxWidth="15rem">
                <Select
                  placeholder={`Filter Acquirer`}
                  onChange={handleAcquirerChange}
                  name={'orderAcquirer'}
                  value={acquirer}
                  bg="black"
                  color="gray"
                  size={'sm'}
                  rounded={'lg'}
                  _focus={{
                    border: '1px solid #81EBAB',
                    boxShadow: 'none'
                  }}
                >
                  {Object.keys(ORDER_ACQUIRER).map((acquirer) => (
                    <option value={acquirer} key={acquirer}>
                      {acquirer}
                    </option>
                  ))}
                </Select>
              </Box>
              <Grid
                gridTemplateColumns={'1fr auto'}
                gap={2}
                alignItems={'center'}
              >
                <Text color={'white'} fontSize={'0.95rem'}>
                  Orders stuck for more than{' '}
                </Text>
                <Box maxWidth="15rem">
                  <Select
                    onChange={handleOrdersInPastHours}
                    name={'pastHourOrders'}
                    value={ordersInPastHours}
                    bg="black"
                    color="gray"
                    size={'sm'}
                    rounded={'lg'}
                    _focus={{
                      border: '1px solid #81EBAB',
                      boxShadow: 'none'
                    }}
                  >
                    {FILTER_BY_PAST_HOURS.map((pastHours) => (
                      <option key={`hours-${pastHours}`} value={pastHours}>
                        {!pastHours ? 'All orders' : pastHours + ' hours'}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Grid>
            </VStack>
          </HStack>
          <Divider style={{ margin: '0' }} borderColor="whiteAlpha.300" />
          <OrdersList
            orders={orders[activeFunnel]?.data}
            pageNo={searchReqData[activeFunnel]?.pageNo || 0}
            totalPages={orders[activeFunnel]?.totalPages}
            handlePageChange={handlePageChange}
            onOrderDetailsClick={onOrderDetailsClick}
            show={'user'}
            orderResource={ORDER_RESOURCE.GOMS_SERVICE}
          />
        </VStack>

        <Divider
          style={{ margin: '0' }}
          borderColor="whiteAlpha.300"
          orientation="vertical"
        />

        {!isMobile && (
          <Box m={0} alignSelf={'flex-start'}>
            {selectedOrder && (
              <OrderDetailsCard
                userId={selectedOrder.userId}
                orderId={selectedOrder.orderId}
                orderResource={selectedOrder.orderResource}
                isSidebar
                showNavLink
              />
            )}
          </Box>
        )}
      </Grid>
    </ContentLayout>
  );
};

export default GomsOrderFunnel;
